/*
 *   File : profile.js
 *   Author : https://evoqins.com
 *   Description : profile component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import { Chip, Loader } from "../../../Components/Other";
import Icon from "../../../Components/icons";
import { PrimaryButton } from "../../../Components/Buttons";
import { ViewModal } from "../../../Components/Modals";

export default function Profile({ userDetail }) {
  const [profile, setProfile] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({
    url: "",
    title: "",
  });

  useEffect(() => {
    if (userDetail) {
      const profileData = userDetail.profile || {};
      const kycDetails = userDetail.kyc_details || {};
      const bankDetails = userDetail.all_banks || [];
      const mandateDetails = userDetail.mandate_details || [];
      const uploadedDocs = userDetail.uploaded_documents || {};

      // Map personal information
      const personal = {
        name: profileData.name || "N/A",
        dob: profileData.dob || "N/A",
        marital_status: profileData.marital_status || "N/A",
        email: profileData.email || "N/A",
        phone: profileData.phone || "N/A",
        gender: profileData.gender || "N/A",
        occupation: profileData.occupation || "N/A",
        source_of_income: profileData.income_source || "N/A",
      };

      // Map ID & Address details
      const permanentAddr = kycDetails.permanent_address || {};
      const details = {
        pan: kycDetails.pan || "N/A",
        nationality: kycDetails.citizenship || "N/A",
        permanent_address: permanentAddr.address_line1
          ? `${permanentAddr.address_line1}, ${permanentAddr.address_line2 || ""}, ${permanentAddr.city || ""}, ${permanentAddr.state || ""}, ${permanentAddr.country || ""} - ${permanentAddr.pincode || ""}`
          : "N/A",
        communication_address: kycDetails.is_same_corresponding_address
          ? "Same as Permanent Address"
          : Object.keys(kycDetails.correspondence_address || {}).length > 0
            ? `${kycDetails.correspondence_address.address_line1}, ${kycDetails.correspondence_address.address_line2 || ""}, ${kycDetails.correspondence_address.city || ""}, ${kycDetails.correspondence_address.state || ""}, ${kycDetails.correspondence_address.country || ""} - ${kycDetails.correspondence_address.pincode || ""}`
            : "N/A",
        kyc_status: kycDetails.kyc_status || "N/A",
      };

      // Map bank details
      const mappedBankDetails = bankDetails.map((bank) => ({
        bank_name: bank.name || "N/A",
        masked_account: bank.account_number || "N/A",
        type: bank.is_default ? "Primary" : "Secondary",
        logo: bank.image || "",
        ifsc: bank.ifsc || "N/A",
        branch: bank.branch || "N/A",
        account_type: bank.account_type || "N/A",
        is_verified: bank.is_verified || "N/A",
        account_holder: bank.account_holder || "N/A",
      }));

      // Map mandate details - using first mandate if available
      const mandate = mandateDetails.length > 0 ? mandateDetails[0] : {};
      const mappedMandateDetails = {
        mandate_id: mandate.mandate_id || "N/A",
        amount: mandate.mandate_limit || "N/A",
        created_on: mandate.created_on || "N/A",
        status: mandate.status || "N/A",
        type: mandate.type || "N/A",
        next_sip_date: mandate.next_sip_date || "N/A",
        bank_name: mandate.bank_name || "N/A",
      };

      // Map documents
      const documents = [];
      if (uploadedDocs.pan_image) {
        documents.push({
          name: "PAN Card",
          icon: "image",
          url: uploadedDocs.pan_image,
        });
      }
      if (uploadedDocs.passport_image) {
        documents.push({
          name: "Passport",
          icon: "image",
          url: uploadedDocs.passport_image,
        });
      }
      if (uploadedDocs.bank_passbook) {
        documents.push({
          name: "Bank Passbook",
          icon: "image",
          url: uploadedDocs.bank_passbook,
        });
      }
      if (uploadedDocs.blank_cheque) {
        documents.push({
          name: "Blank Cheque",
          icon: "image",
          url: uploadedDocs.blank_cheque,
        });
      }

      setProfile({
        personal,
        details,
        bankDetails: mappedBankDetails,
        mandateDetails: mappedMandateDetails,
        documents,
      });
    }
  }, [userDetail]);

  // helper to convert keys → Title Case
  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Helper to open document in modal
  const handleViewDocument = (url, name) => {
    if (url && url !== "N/A") {
      setSelectedDocument({ url, title: name });
      setModalOpen(true);
    }
  };

  // Handler to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDocument({ url: "", title: "" });
  };

  if (!profile) {
    return <Loader />;
  }

  return (
    <div className="d-flex flex-column rg-48 e-glass-card rounded-32 p-24">
      {/* ----------------------------------- PERSONAL ---------------------------------- */}
      <div className="d-flex flex-column bg-facebook-blue bg-opacity-7 p-3 rounded-16">
        <h3 className="fw-500 fs-16 lh-1 text-jet-gray">
          Personal Information
        </h3>

        <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-2  rg-24 pt-3">
          {Object.entries(profile?.personal || {}).map(([key, value]) => (
            <div key={key} className="col">
              <div className="d-flex flex-column">
                <h4 className="fs-14 fw-400 text-davys-gray lh-16 text-capitalize">
                  {formatKey(key)}
                </h4>
                <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 text-break">
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ----------------------------------- DETAILS ---------------------------------- */}
      <div className="d-flex flex-column bg-medium-sea-green bg-opacity-7 p-3 rounded-16">
        <h3 className="fw-500 fs-16 lh-1 text-jet-gray">ID & Address</h3>

        <div className="d-flex flex-wrap rg-24 col-g-32 pt-3">
          {Object.entries(profile?.details || {}).map(([key, value]) => (
            <div key={key} className="d-flex flex-column">
              <h4 className="fs-14 fw-400 text-davys-gray lh-16 text-capitalize">
                {formatKey(key)}
              </h4>
              <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ----------------------------------- BANK DETAILS ---------------------------------- */}
      <div className="d-flex flex-column gap-3 bg-copper-red bg-opacity-7 p-3 rounded-16">
        <h3 className="fw-500 fs-16 lh-1 text-jet-gray">Bank Details</h3>

        <div className="d-flex col-g-32 rg-24 flex-wrap">
          {profile?.bankDetails?.map((bank, index) => (
            <div
              key={index}
              className="d-flex e-glass-card rounded-12 px-3 py-12 column-gap-2 align-items-center"
            >
              {bank.logo && (
                <div className="e-glass-card rounded-8 p-2">
                  <img
                    width={16}
                    src={bank.logo}
                    alt={bank.bank_name}
                    onError={(e) => {
                      e.target.src = require("../../../Assets/Images/bank.png");
                    }}
                  />
                </div>
              )}

              <p className="mb-0 fw-400 fs-12 lh-16 text-eerie-black">
                {bank.bank_name}
              </p>

              <p className="mb-0 fw-400 fs-10 lh-1 text-davys-gray">
                {bank.masked_account}
              </p>

              {bank.type === "Primary" && (
                <Chip
                  text="Primary"
                  style="p-2 bg-lavender-blue bg-opacity-43"
                  textClr="text-facebook-blue"
                />
              )}
            </div>
          ))}
        </div>

        {/* Mandate */}
        <h3 className="fw-500 fs-16 lh-1 text-jet-gray mt-2">
          Mandate Details
        </h3>

        <div className="d-flex col-g-32 flex-wrap">
          {Object.entries(profile?.mandateDetails || {}).map(([key, value]) => (
            <div key={key} className="d-flex flex-column">
              <h4 className="fs-14 fw-400 text-davys-gray lh-16 text-capitalize">
                {formatKey(key)}
              </h4>
              <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ----------------------------------- DOCUMENTS ---------------------------------- */}
      {profile?.documents && profile.documents.length > 0 && (
        <div className="d-flex flex-column bg-medium-sea-green bg-opacity-7 p-3 rounded-16 row-gap-3">
          <h3 className="fw-500 fs-16 lh-1 text-jet-gray">Documents</h3>

          <div className="row row-cols-lg-3 row-cols-md-2 row-cols-1 rg-24">
            {profile?.documents?.map((doc, index) => (
              <div className="col" key={index}>
                <div className="d-flex e-glass-card justify-content-between rounded-12 px-3 py-12 align-items-center">
                  <div className="d-flex align-items-center">
                    <Icon icon={doc.icon} size="20px" />
                    <p className="mb-0 fw-400 fs-12 ps-12 lh-16 text-eerie-black">
                      {doc.name}
                    </p>
                  </div>

                  <PrimaryButton
                    ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
                    name="View"
                    onPress={() => handleViewDocument(doc.url, doc.name)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Document Modal */}
      <ViewModal
        open={modalOpen}
        imageUrl={selectedDocument.url}
        title={"View Document"}
        onClose={handleCloseModal}
      />
    </div>
  );
}
